/**
 * Registration page with full-name, email, optional company, and password
 * fields. Includes a real-time password strength indicator.
 *
 * @module RegisterPage
 */

import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Link,
  alpha,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../api';
import { GREEN, DARK_BLUE } from '../theme';
import { FloatingOrb, PageTransition } from '../components/AnimatedComponents';

/**
 * Score a password on a 0–100 scale based on length, uppercase,
 * digits, and special characters.
 */
function getPasswordStrength(pwd: string): { score: number; label: string; color: string } {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { score: 20, label: 'Weak', color: '#f44336' };
  if (score <= 2) return { score: 40, label: 'Fair', color: '#ff9800' };
  if (score <= 3) return { score: 60, label: 'Good', color: '#ffc107' };
  if (score <= 4) return { score: 80, label: 'Strong', color: GREEN };
  return { score: 100, label: 'Very Strong', color: '#2d9c1c' };
}

/** Renders the registration form with a password-strength meter and redirects on success. */
export default function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    company: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError(t('register.errorMismatch'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('register.errorMinLength'));
      return;
    }

    setLoading(true);
    try {
      await register(form.email, form.password, form.fullName, form.company || undefined);
      navigate('/submit-project');
    } catch (err: any) {
      setError(getErrorMessage(err, t('register.error')));
    } finally {
      setLoading(false);
    }
  };

  const strength = getPasswordStrength(form.password);

  const strengthLabels: Record<string, string> = {
    'Weak': t('register.strengthWeak'),
    'Fair': t('register.strengthFair'),
    'Good': t('register.strengthGood'),
    'Strong': t('register.strengthStrong'),
    'Very Strong': t('register.strengthVeryStrong'),
  };

  return (
    <PageTransition>
      <SEO
        title={t('register.seoTitle')}
        description={t('register.seoDesc')}
        canonical="/register"
      />
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          py: 4,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(GREEN, 0.03)} 0%, ${alpha(DARK_BLUE, 0.04)} 100%)`,
        }}
      >
        <FloatingOrb size={400} color={DARK_BLUE} top={-100} left={-80} />
        <FloatingOrb size={300} color={GREEN} bottom={-80} right={-60} animationDelay="3s" />

        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
                border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${DARK_BLUE}, ${GREEN})`,
                }}
              />

              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2.5,
                    }}
                  >
                    <RocketLaunchIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                </motion.div>
                <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 700 }}>
                  {t('register.title')}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '1rem' }}>
                  {t('register.subtitle')}
                </Typography>
              </Box>

              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                  <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                </motion.div>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label={t('register.fullName')}
                  value={form.fullName}
                  onChange={handleChange('fullName')}
                  required
                  sx={{ mb: 2.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label={t('register.email')}
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  required
                  sx={{ mb: 2.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label={t('register.company')}
                  value={form.company}
                  onChange={handleChange('company')}
                  sx={{ mb: 2.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label={t('register.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange('password')}
                  required
                  helperText={t('register.passwordHint')}
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {form.password.length > 0 && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{t('register.passwordStrength')}</Typography>
                        <Typography variant="caption" sx={{ color: strength.color, fontWeight: 600 }}>
                          {strengthLabels[strength.label] || strength.label}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={strength.score}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: alpha(strength.color, 0.12),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 2,
                            bgcolor: strength.color,
                          },
                        }}
                      />
                    </Box>
                  </motion.div>
                )}
                <TextField
                  fullWidth
                  label={t('register.confirmPassword')}
                  type={showPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  required
                  sx={{ mb: 3, mt: form.password.length > 0 ? 0 : 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={loading}
                  sx={{ mb: 3, py: 1.5, borderRadius: 3, fontSize: '1rem' }}
                >
                  {loading ? t('register.submitting') : t('register.submit')}
                </Button>
              </form>

              <Typography align="center" variant="body2" color="text.secondary">
                {t('register.hasAccount')}{' '}
                <Link component={RouterLink} to="/login" sx={{ fontWeight: 600 }}>
                  {t('register.signIn')}
                </Link>
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </PageTransition>
  );
}
